import {
    BADGES,
    BUCKETS,
    INTEGRATION_BADGE_LABELS,
    RELATIONSHIP_SUBTITLES,
    REVIEW_DECISIONS,
    SHIPPER_BADGES,
} from './constants';

/** The account a row describes: `carrier` on organization_carrier_statuses, `shipper` on the shipper view. */
const accountOf = (row) => row.carrier || row.shipper;

const SEARCHABLE = (row) => {
    const account = accountOf(row);
    return [account?.business_name, account?.email, account?.user?.email, row.carrier_id || row.shipper_id];
};

const byName = (a, b) => (accountOf(a)?.business_name || '').localeCompare(accountOf(b)?.business_name || '');

/** Rows whose name, email or account ID contains `search`, case-insensitively. Every row when `search` is empty. */
export const filterRows = (rows, search) => {
    const query = (search || '').trim().toLowerCase();
    if (!query) return rows || [];
    return (rows || []).filter((row) => SEARCHABLE(row).some((value) => value?.toLowerCase().includes(query)));
};

/** No filters picked: every row shows. */
export const NO_FILTERS = { types: [], badges: [] };

/**
 * Rows matching the onboarding filter. `types` are relationships (OWNED, PARTNER, CONTRACTOR) and `badges` are badge
 * keys. Within a group a row needs any one of the picks; across groups it needs both. An empty group doesn't filter.
 */
export const filterByFacets = (rows, { types = [], badges = [] } = NO_FILTERS, badgeList = BADGES) =>
    rows.filter((row) => {
        if (types.length && !types.includes(row.relationship)) return false;
        if (badges.length && !badgeList.some((badge) => badges.includes(badge.key) && row[badge.flag])) return false;
        return true;
    });

/** The rows in one tab, sorted by name. */
export const rowsInBucket = (rows, bucket) => {
    const inBucket = rows.filter((row) => row[bucket.flag]);
    return inBucket.sort(byName);
};

/** `{ [bucket key]: row count }` for every tab. */
export const bucketCounts = (rows, buckets = BUCKETS) =>
    Object.fromEntries(
        buckets.map((bucket) => [bucket.key, rows.reduce((count, row) => (row[bucket.flag] ? count + 1 : count), 0)])
    );

/** The card subtitle for a row's relationship to the org. */
export const subtitleFor = (row) => RELATIONSHIP_SUBTITLES[row.relationship] || '';

/**
 * The contractor's most recent compliance decision (APPROVED or DENIED), or null. A PENDING review from a document
 * upload isn't a decision, so it's skipped. Reviews arrive newest first.
 */
export const latestDecision = (row) =>
    row.compliance_reviews.find((review) => review.status === 'APPROVED' || review.status === 'DENIED') || null;

/** Longest badge tooltip, in characters, before it's cut short with "...". The dialogs show the full note. */
const TOOLTIP_MAX_LENGTH = 50;

const truncateTooltip = (text) =>
    text.length > TOOLTIP_MAX_LENGTH ? `${text.slice(0, TOOLTIP_MAX_LENGTH).trimEnd()}...` : text;

/** The note on the contractor's latest decision with this status, for a badge tooltip. */
const decisionNote = (row, status) => {
    const review = row.compliance_reviews.find((r) => r.status === status);
    if (!review) return 'No note on record.';
    return truncateTooltip(review.note || 'No note was left.');
};

const BADGE_TOOLTIP_STATUS = { APPROVED: 'APPROVED', REJECTED: 'DENIED' };

/**
 * The badges a card shows, in order. Approved By Compliance and Rejected By Compliance carry the decision's note as
 * their tooltip, and the integration badge names the integration's status.
 *
 * @returns {{ key: string, label: string, variant: string, tooltip: string|null }[]}
 */
export const badgesFor = (row) =>
    BADGES.reduce((shown, badge) => {
        if (!row[badge.flag]) return shown;
        const label =
            badge.key === 'INTEGRATION' ? INTEGRATION_BADGE_LABELS[row.integration_status] || badge.label : badge.label;
        const tooltipStatus = BADGE_TOOLTIP_STATUS[badge.key];
        shown.push({
            key: badge.key,
            label,
            variant: badge.variant,
            tooltip: tooltipStatus ? decisionNote(row, tooltipStatus) : null,
        });
        return shown;
    }, []);

/** The address an invite goes to: the client's email, else its user's. */
const inviteEmail = (row) => row.carrier?.email || row.carrier?.user?.email || null;

/** A Fulfillment Partner or Contractor that hasn't signed up can be sent a fresh invite. */
const canReinvite = (row) => ['PARTNER', 'CONTRACTOR'].includes(row.relationship) && !row.logged_in;

/**
 * The card's menu options, in order.
 *
 * @returns {{ key: string, label: string }[]}
 */
export const menuOptionsFor = (row) => {
    const options = [];

    if (row.relationship === 'CONTRACTOR') {
        const review = latestDecision(row);
        if (review) {
            options.push({ key: 'VIEW_DECISION', label: REVIEW_DECISIONS[review.status]?.view || 'View Review' });
        }
        if (row.compliance_reviews.length) {
            options.push({ key: 'VIEW_HISTORY', label: 'View Compliance History' });
        }
    }

    return options;
};

/** Flex Centers and Fulfillment Partners carry the "No Integrations Required" flag. */
const hasIntegrationsFlag = (row) => ['OWNED', 'PARTNER'].includes(row.relationship);

/**
 * What the options panel offers for the selected rows:
 * - `invitable`: Partners and Contractors that haven't signed up and have an email
 * - `reviewable`: contractors, on the Compliance tab only (Deny)
 * - `approvable`: the reviewable contractors with complete documents that aren't already approved (Approve)
 * - `markNoIntegrations`: Flex Centers and Partners still requiring integrations, on the Integrations tab only
 * - `requireIntegrations`: Flex Centers and Partners marked No Integrations Required, on any tab
 */
export const selectionActions = (selectedRows, bucketKey) => {
    const reviewable =
        bucketKey === 'COMPLIANCE' ? selectedRows.filter((row) => row.relationship === 'CONTRACTOR') : [];
    return {
        invitable: selectedRows.filter((row) => canReinvite(row) && inviteEmail(row)),
        reviewable,
        approvable: reviewable.filter((row) => row.docs_complete && row.compliance_status !== 'APPROVED'),
        markNoIntegrations:
            bucketKey === 'INTEGRATIONS'
                ? selectedRows.filter((row) => hasIntegrationsFlag(row) && row.integrations_required)
                : [],
        requireIntegrations: selectedRows.filter((row) => hasIntegrationsFlag(row) && !row.integrations_required),
    };
};

/**
 * Why Approve leaves some or all of the checked contractors out, for its tooltip, or null when it covers them all.
 * The status is the contractor's current one, which the latest decision (or a document upload) sets.
 */
export const approveSkipReason = (reviewable) => {
    const { alreadyApproved, missingDocs } = reviewable.reduce(
        (counts, row) => {
            if (row.compliance_status === 'APPROVED') counts.alreadyApproved += 1;
            else if (!row.docs_complete) counts.missingDocs += 1;
            return counts;
        },
        { alreadyApproved: 0, missingDocs: 0 }
    );
    const skipped = alreadyApproved + missingDocs;
    if (!skipped) return null;

    const parts = [];
    if (alreadyApproved) parts.push(`${alreadyApproved} already approved`);
    if (missingDocs) parts.push(`${missingDocs} with incomplete documents`);

    if (skipped === reviewable.length) {
        if (!alreadyApproved) return 'Documents incomplete';
        if (!missingDocs) return 'Already approved';
        return `Can't approve: ${parts.join(', ')}`;
    }
    return `Skips ${parts.join(' and ')}`;
};

/** A shipper card's badges, in order, shaped like badgesFor's. */
export const shipperBadgesFor = (row) =>
    SHIPPER_BADGES.reduce((shown, badge) => {
        if (row[badge.flag]) shown.push({ key: badge.key, label: badge.label, variant: badge.variant, tooltip: null });
        return shown;
    }, []);

/**
 * What a card shows for its row: carrier rows (the default) or shipper rows.
 *
 * @returns {{ id: string, name: string, subtitle: string, badges: object[], options: object[] }}
 */
export const describeCarrierRow = (row) => ({
    id: row.carrier_id,
    name: row.carrier?.business_name || 'Unnamed carrier',
    subtitle: subtitleFor(row),
    badges: badgesFor(row),
    options: menuOptionsFor(row),
});

/** describeCarrierRow for a shipper row: its email as the subtitle, and no menu. */
export const describeShipperRow = (row) => ({
    id: row.shipper_id,
    name: row.shipper?.business_name || 'Unnamed shipper',
    subtitle: row.shipper?.email || row.shipper?.user?.email || '',
    badges: shipperBadgesFor(row),
    options: [],
});

/**
 * What the shipper options panel offers for the selected rows:
 * - `markNoIntegrations`: shippers still requiring integrations, on the Integrations tab only
 * - `requireIntegrations`: shippers marked No Integrations Required, on any tab
 */
export const shipperSelectionActions = (selectedRows, bucketKey) => ({
    markNoIntegrations: bucketKey === 'INTEGRATIONS' ? selectedRows.filter((row) => row.integrations_required) : [],
    requireIntegrations: selectedRows.filter((row) => !row.integrations_required),
});
